import React from 'react';
import Slider from 'react-slick';
import { Link } from "react-router-dom";
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    Chip,
    Box
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
//import StarIcon from '@mui/icons-material/Star';
import "../styles/CoursesPage.css";

const SliderContainer = styled(Box)(({ theme }) => ({
    margin: '50px auto',
    maxWidth: '1100px',
    padding: '0 20px',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
        margin: '30px auto',
        padding: '0 10px',
        maxWidth: '100%',
        width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
        width: '100%',
        padding: '0 5px',
    },
    // Custom arrow styling
    '& .slick-arrow': {
        fontSize: '24px',
        width: '50px',
        height: '50px',
        zIndex: 2,
        '&:before': {
            fontSize: '24px',
            color: '#667eea',
        },
    },
    '& .slick-prev': {
        left: '-60px',
        [theme.breakpoints.down('md')]: {
            left: '-40px',
        },
        [theme.breakpoints.down('sm')]: {
            left: '10px',
        },
    },
    '& .slick-next': {
        right: '-60px',
        [theme.breakpoints.down('md')]: {
            right: '-40px',
        },
        [theme.breakpoints.down('sm')]: {
            right: '10px',
        },
    },
    '& .slick-dots': {
        bottom: '-50px',
        '& li button:before': {
            fontSize: '12px',
            color: '#667eea',
        },
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '2.5rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: '40px',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80px',
        height: '4px',
        background: 'linear-gradient(90deg, #667eea, #764ba2)',
        borderRadius: '2px',
    },
}));

const StyledCard = styled(Card)(({ theme, bgcolor }) => ({
    height: '300px',
    margin: '0 6px',
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${bgcolor}40, ${bgcolor}80)`,
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
        transform: 'translateY(-6px) scale(1.02)',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${bgcolor}20, ${bgcolor}40)`,
        zIndex: 0,
    },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
    height: '130px',
    position: 'relative',
    zIndex: 1,
    transition: 'transform 0.3s ease',
    flexShrink: 0,
    '&:hover': {
        transform: 'scale(1.05)',
    },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    padding: '10px',
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 0,
}));

const CourseTitle = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: '4px',
    lineHeight: '1.2',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    minHeight: '2.4em',
}));

const DateChip = styled(Chip)(({ theme }) => ({
    margin: '1px',
    fontSize: '0.6rem',
    height: '18px',
    '& .MuiChip-icon': {
        fontSize: '9px',
    },
    '& .MuiChip-label': {
        padding: '0 4px',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '14px',
    padding: '5px 10px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'none',
    color: 'white',
    border: 'none',
    transition: 'all 0.3s ease',
    minWidth: 'auto',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    },
}));

const CourseSlider = ({courses}) => {
    // "mamografi 14 mayıs test" kursunu filtrele
    const filteredCourses = courses?.filter(course =>
        course.name.toLowerCase() !== 'mamografi 14 mayıs test'
    ) || [];

    const colors = [
        '#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a',
        '#ffecd2', '#a8edea', '#d299c2', '#fce38a', '#f38ba8'
    ];

    const getSliderSettings = (courseCount) => ({
        dots: true,
        infinite: false, // Infinite kaydırmayı kapat
        speed: 600,
        slidesToShow: Math.min(2, courseCount), // Maksimum 2 kutucuk, kurs sayısından az olamaz
        slidesToScroll: 1,
        autoplay: false, // Autoplay'i kapat
        autoplaySpeed: 3500,
        pauseOnHover: true,
        arrows: courseCount > 2, // Sadece 2'den fazla kurs varsa ok göster
        centerMode: false,
        variableWidth: false,
        dotsClass: "slick-dots custom-dots",

        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: Math.min(2, courseCount), // Maksimum 2 kutucuk
                    slidesToScroll: 1,
                    infinite: false,
                    dots: true,
                    arrows: courseCount > 2
                }
            },
            {
                breakpoint: 900,
                settings: {
                    slidesToShow: Math.min(2, courseCount), // Maksimum 2 kutucuk
                    slidesToScroll: 1,
                    infinite: false,
                    dots: true,
                    arrows: courseCount > 2
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1, // Küçük ekranlarda 1 kutucuk
                    slidesToScroll: 1,
                    infinite: false,
                    dots: courseCount > 1,
                    arrows: courseCount > 1
                }
            },
            {
                breakpoint: 400,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: false,
                    dots: courseCount > 1,
                    centerMode: false,
                    centerPadding: '0px',
                    arrows: courseCount > 1
                }
            }
        ]
    });

    return (
        <SliderContainer>
            <SectionTitle>
                🎓 POPÜLER KURSLAR
            </SectionTitle>

            <Slider {...getSliderSettings(filteredCourses?.length || 0)}>
                {filteredCourses?.map((course, index) => (
                    <Box key={course.id} sx={{ padding: '0 5px' }}>
                        <StyledCard bgcolor={colors[index % colors.length]}>
                            <StyledCardMedia
                                component="img"
                                image={course.imageUrl || "/images/mamografi_drawing.jpg"}
                                alt={course.name}
                            />

                            <StyledCardContent>
                                <Box>
                                    <CourseTitle>
                                        {course.name}
                                    </CourseTitle>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                        <DateChip
                                            icon={<CalendarTodayIcon />}
                                            label={course.startDate}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                        <DateChip
                                            icon={<AccessTimeIcon />}
                                            label={course.endDate}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <StyledButton
                                        component={Link}
                                        to={`/course-details/${course.id}`}
                                        endIcon={<ArrowForwardIcon />}
                                    >
                                        Detaylar
                                    </StyledButton>

                                </Box>
                            </StyledCardContent>
                        </StyledCard>
                    </Box>
                ))}
            </Slider>
        </SliderContainer>
    );
};

export default CourseSlider;
